import { styled } from '@linaria/react';
import { forwardRef } from 'react';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';
import { ContentPlaceholder } from '../../../../../../components/content-placeholder/content-placeholder';
import { useCostTabStore } from '../../../../../../store/strategy-store/strategy-store';
import {
  setCostSelectedEpic,
  setCostSelectedInitiative,
  setCostSelectedTeam,
  setPerformanceActivePanel,
} from '../../../../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../../../../store/strategy-store/strategy-store.type';
import { baseWhite } from '../../../../../../styles/design-tokens';
import {
  useChartData,
  useCostDataFetched,
  useCostEpics,
  useCostInitiatives,
  useCostTeams,
  useFiltersApplied,
} from '../../cost-tab.hooks';
import { ChartDataType, CostMode } from '../../cost-tab.type';
import { TreeMapContent } from './tree-map-content';
import { TreeMapTooltip } from './tree-map-tooltip';

export const TreeMapChart = forwardRef<HTMLDivElement>((_, ref) => {
  const mode = useCostTabStore((state) => state.mode);

  const epics = useCostEpics();
  const teams = useCostTeams();
  const initiatives = useCostInitiatives();

  const filtersApplied = useFiltersApplied();
  const dataFetched = useCostDataFetched();
  const chartData = useChartData();

  const handleClick = (data: any) => {
    const payload = data.payload || data;
    if (!payload) {
      return;
    }

    // Handle "Other" tile clicks
    if (payload.type === ChartDataType.Other) {
      if (mode === CostMode.ContributingEpics) {
        setCostSelectedEpic(null);
        setPerformanceActivePanel(Panel.OtherEpics);
      } else if (mode === CostMode.ContributingTeams) {
        setCostSelectedTeam(null);
        setPerformanceActivePanel(Panel.OtherTeams);
      } else if (mode === CostMode.ContributingInitiatives) {
        setCostSelectedInitiative(null);
        setPerformanceActivePanel(Panel.OtherInitiatives);
      }
      return;
    }

    // Handle individual epic/team clicks
    if (mode === CostMode.ContributingEpics) {
      setCostSelectedEpic(epics.find((epic) => epic.id === payload.id) ?? null);
    } else if (mode === CostMode.ContributingTeams) {
      setCostSelectedTeam(teams.find((team) => team.id === payload.id) ?? null);
    } else if (mode === CostMode.ContributingInitiatives) {
      setCostSelectedInitiative(initiatives.find((initiative) => initiative.id === payload.id) ?? null);
    }

    setPerformanceActivePanel(Panel.Side);
  };

  if (!dataFetched) {
    return <ContentPlaceholder />;
  }

  if (!filtersApplied) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  return (
    <StyledResponsiveContainer ref={ref}>
      <Treemap
        data={chartData}
        dataKey="size"
        aspectRatio={1}
        stroke={baseWhite}
        isAnimationActive={false}
        content={<TreeMapContent />}
        onClick={handleClick}
      >
        <Tooltip content={<TreeMapTooltip />} />
      </Treemap>
    </StyledResponsiveContainer>
  );
});

const StyledResponsiveContainer = styled(ResponsiveContainer)`
  .recharts-wrapper {
    position: absolute !important;
  }
`;
